import { ProcessedMetrics, Place, ConsolidatedMetrics } from '@/types';
import { calculateGrowth } from '@/utils/metrics';
import { formatPlaceName } from '@/lib/utils';
type Unit = 'persons' | 'units' | 'ratio' | 'percent';

interface MetricData {
  value: string;
  unit: Unit;
  changePercent: string;
  changeType: 'positive' | 'negative';
}

interface BarData {
  label: string;
  value: number;
}

interface ChartData {
  data: BarData[];
  unit: Unit;
  title: string;
  description: string;
}

interface SnapshotData {
  metrics: {
    population: MetricData;
    housingStock: MetricData;
    unbuiltPermits: MetricData;
    priceToIncome: MetricData;
    rentalVacancy: MetricData;
    multiFamilyShare: MetricData;
  };
  charts: {
    populationGrowth: ChartData;
    housingStockGrowth: ChartData;
    housingStockByDecade: ChartData;
    housingStockComposition: {
      legendItems: {
        label: string;
        percentage: string;
        color: string;
        value: number;
        growth: number | null;
      }[];
      unit: Unit;
      title: string;
      description: string;
      totalHousingUnits: number;
    };
  };
}

const formatValue = (value: number, unit: Unit): string => {
  switch (unit) {
    case 'persons':
      return new Intl.NumberFormat().format(Math.round(value));
    case 'units':
      return new Intl.NumberFormat().format(value);
    case 'ratio':
      return `${value.toFixed(1)}x`;
    case 'percent':
      return `${value.toFixed(1)}%`;
    default:
      return value.toString();
  }
};

const safeNumber = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  }
  if (value && typeof value === 'object') {
    if ('value' in value) return safeNumber(value.value);
    if ('growth' in value) return safeNumber(value.growth);
  }
  return 0;
};

const calculateYoYChange = (current: number, previous: number): string => {
  if (!previous || current === null || previous === null) return 'No data';
  const change = ((current - previous) / previous) * 100;
  return `${change.toFixed(1)}%`;
};

const determineChangeType = (current: number, previous: number): 'positive' | 'negative' => {
  if (!previous || current === null || previous === null) return 'positive';
  const change = current - previous;
  return change >= 0 ? 'positive' : 'negative';
};

export const useSnapshotData = (
  selectedPlace: Place | null,
  selectedPlaceMetrics: ProcessedMetrics[],
  peerMetrics: { peer: Place; data: ProcessedMetrics[] }[]
): SnapshotData | null => {
  if (!selectedPlace || !selectedPlaceMetrics?.length) {
    return null;
  }

  const latestMetrics = selectedPlaceMetrics[0];
  const previousMetrics = selectedPlaceMetrics[1] || latestMetrics;

  const createLegendItem = (
    label: string,
    color: string,
    metricKey: keyof ConsolidatedMetrics,
  ) => {
    const currentValue = safeNumber(latestMetrics[metricKey]);
    const previousValue = safeNumber(previousMetrics[metricKey]);
    const totalUnits = safeNumber(latestMetrics.total_housing_units);
    
    const percentageOfTotal = totalUnits > 0 ? (currentValue / totalUnits) * 100 : 0;

    return {
      label,
      percentage: formatValue(percentageOfTotal, 'percent'),
      color,
      value: currentValue,
      growth: calculateGrowth(currentValue, previousValue)
    };
  };

  return {
    metrics: {
      population: {
        value: formatValue(safeNumber(latestMetrics.total_population), 'persons'),
        unit: 'persons',
        changePercent: calculateYoYChange(
          safeNumber(latestMetrics.total_population),
          safeNumber(previousMetrics.total_population)
        ),
        changeType: determineChangeType(
          safeNumber(latestMetrics.total_population),
          safeNumber(previousMetrics.total_population)
        )
      },
      housingStock: {
        value: formatValue(safeNumber(latestMetrics.total_housing_units), 'units'),
        unit: 'units',
        changePercent: calculateYoYChange(
          safeNumber(latestMetrics.total_housing_units),
          safeNumber(previousMetrics.total_housing_units)
        ),
        changeType: determineChangeType(
          safeNumber(latestMetrics.total_housing_units),
          safeNumber(previousMetrics.total_housing_units)
        )
      },
      unbuiltPermits: {
        value: formatValue(safeNumber(latestMetrics.bps_total_units), 'units'),
        unit: 'units',
        changePercent: calculateYoYChange(
          safeNumber(latestMetrics.bps_total_units),
          safeNumber(previousMetrics.bps_total_units)
        ),
        changeType: determineChangeType(
          safeNumber(latestMetrics.bps_total_units),
          safeNumber(previousMetrics.bps_total_units)
        )
      },
      priceToIncome: {
        value: formatValue(safeNumber(latestMetrics.price_to_income), 'ratio'),
        unit: 'ratio',
        changePercent: calculateYoYChange(
          safeNumber(latestMetrics.price_to_income),
          safeNumber(previousMetrics.price_to_income)
        ),
        changeType: determineChangeType(
          safeNumber(latestMetrics.price_to_income),
          safeNumber(previousMetrics.price_to_income)
        )
      },
      rentalVacancy: {
        value: formatValue(safeNumber(latestMetrics.rental_vacancy_rate), 'percent'),
        unit: 'percent',
        changePercent: calculateYoYChange(
          safeNumber(latestMetrics.rental_vacancy_rate),
          safeNumber(previousMetrics.rental_vacancy_rate)
        ),
        changeType: determineChangeType(
          safeNumber(latestMetrics.rental_vacancy_rate),
          safeNumber(previousMetrics.rental_vacancy_rate)
        )
      },
      multiFamilyShare: {
        value: formatValue(safeNumber(latestMetrics.pct_mf_existing), 'percent'),
        unit: 'percent',
        changePercent: calculateYoYChange(
          safeNumber(latestMetrics.pct_mf_existing),
          safeNumber(previousMetrics.pct_mf_existing)
        ),
        changeType: determineChangeType(
          safeNumber(latestMetrics.pct_mf_existing),
          safeNumber(previousMetrics.pct_mf_existing)
        )
      }
    },
    charts: {
      populationGrowth: {
        data: (() => {
          const fiveYearMetrics = selectedPlaceMetrics.slice(0, 5).reverse();
          const growthValues = fiveYearMetrics.map(m => safeNumber(m.population_growth) * 100); // Convert decimal to percentage
          
          return fiveYearMetrics.map((metric, index) => ({
            label: metric.year.toString(),
            value: growthValues[index]
          }));
        })(),
        unit: 'percent',
        title: "Population Growth",
        description: `Annual population growth rates in ${formatPlaceName(selectedPlace.name)} from ${selectedPlaceMetrics[4]?.year || (latestMetrics.year - 4)} to ${latestMetrics.year}`
      },
      housingStockGrowth: {
        data: selectedPlaceMetrics.slice(0, 5).reverse().map((metric) => {
          const value = safeNumber(metric.housing_stock_growth_if_permits_built_instantly); // Value is already a percentage
          return {
            label: metric.year.toString(),
            value
          };
        }),
        unit: 'percent',
        title: "Housing Stock Growth",
        description: `Percentage increase in total housing units if all permits were built instantly in ${formatPlaceName(selectedPlace.name)} as of ${latestMetrics.year}`
      },
      housingStockByDecade: (() => {
        // Get absolute values for each period
        const values = [
          safeNumber(latestMetrics.units_built_1939_or_earlier) +
          safeNumber(latestMetrics.units_built_between_1940_and_1949) +
          safeNumber(latestMetrics.units_built_between_1950_and_1959),
          safeNumber(latestMetrics.units_built_between_1960_and_1969),
          safeNumber(latestMetrics.units_built_between_1970_and_1979),
          safeNumber(latestMetrics.units_built_between_1980_and_1989),
          safeNumber(latestMetrics.units_built_between_1990_and_1999),
          safeNumber(latestMetrics.units_built_between_2000_and_2009),
          safeNumber(latestMetrics.units_built_between_2010_and_2013) +
          safeNumber(latestMetrics.units_built_2014_or_later)
        ];
        
        return {
          data: [
            { label: '<1960', value: values[0] },
            { label: '1960', value: values[1] },
            { label: '1970', value: values[2] },
            { label: '1980', value: values[3] },
            { label: '1990', value: values[4] },
            { label: '2000', value: values[5] },
            { label: '2010+', value: values[6] }
          ],
          unit: 'units' as Unit,
          title: "Housing Stock by Decade",
          description: `Number of existing housing structures by decade built in ${formatPlaceName(selectedPlace.name)} as of ${latestMetrics.year}`
        } as ChartData;
      })(),
      housingStockComposition: {
        legendItems: [
          createLegendItem('50+ Units', 'rgba(202,0,121,1)', '50_plus_unit_structures'),
          createLegendItem('20-49 Units', 'rgba(0,192,217,1)', '20_49_unit_structures'),
          createLegendItem('10-19 Units', 'rgba(243,186,211,1)', '10_19_unit_structures'),
          createLegendItem('5-9 Units', 'rgba(241,171,17,1)', '5_9_unit_structures'),
          createLegendItem('3-4 Units', 'rgba(59,80,162,1)', '3_4_unit_structures'),
          createLegendItem('2 Units', 'rgba(235,111,17,1)', '2_unit_structures'),
          createLegendItem('1 Unit (Detached)', 'rgba(194,175,240,1)', '1_unit_detached'),
          createLegendItem('1 Unit (Attached)', 'rgba(129,190,9,1)', '1_unit_attached'),
        ],
        unit: 'percent',
        title: "Housing Stock Composition",
        description: `Housing units by structure size in ${formatPlaceName(selectedPlace.name)} as of ${latestMetrics.year}`,
        totalHousingUnits: safeNumber(latestMetrics.total_housing_units),
      }
    }
  };
}; 